import { Injectable } from '@angular/core';
import {
	StateContext,
	State,
	NgxsOnInit,
	Action,
	Select,
	Selector,
} from '@ngxs/store';
import {
	ConnectedUser,
	TitlePlusStateModel,
	defaultTitlePlusStateModel,
} from './title-plus.models';
import { BusinessUser } from '@models/businesses';
import { TitlePlusTokensService } from '@repos/title-plus/title-plus-tokens.service';
import { AuthState } from '@state/auth';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import LogRocket from 'logrocket';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { Loading, Loaded, Errored } from './title-plus.actions';
import { getFullName } from '@core/util/string';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { UserProfileState } from '@state/user-profile';
import { MatterBusinessUser } from '@models/matter';

type Ctx = StateContext<TitlePlusStateModel>;

@State<TitlePlusStateModel>({
	name: 'titlePlusState',
	defaults: { ...defaultTitlePlusStateModel },
})
@Injectable()
export class TitlePlusState implements NgxsOnInit {
	@Select(UserProfileState.getCurrentBusinessId)
	businessId$!: Observable<string>;

	constructor(private titlePlusTokens: TitlePlusTokensService) {}

	ngxsOnInit({ dispatch }: Ctx): void {
		this.businessId$
			.pipe(
				distinctUntilChanged(),
				switchMap((id) => {
					dispatch(new Loading());
					if (id) {
						return this.titlePlusTokens.All();
					}

					return of([]);
				}),
				map((data) => data.map((token) => token.id)),
			)
			.subscribe(
				(connectedIds) => dispatch(new Loaded(connectedIds)),
				(err) => dispatch(new Errored(err)),
			);
	}

	@Selector([TitlePlusState])
	static connectedIds(state: TitlePlusStateModel): string[] {
		return state.connectedIds || [];
	}

	@Selector([
		TitlePlusState.connectedIds,
		BusinessUsersState.getLawyers,
		BusinessUsersState.getLoggedInBusinessUser,
	])
	static connectedLawyers(
		connectedIds: string[],
		lawyers: BusinessUser[],
		loggedInUser: BusinessUser,
	): ConnectedUser[] {
		if (!loggedInUser?.admin) {
			const lawyer = lawyers.find((lawyer) => lawyer.id === loggedInUser?.id);
			if (lawyer) {
				const connected = connectedIds.includes(lawyer.id);
				return [
					{
						...lawyer,
						connected,
						name: getFullName([lawyer.firstName, lawyer.lastName]),
					},
				];
			}
		}
		return lawyers?.map((lawyer) => {
			const connected = connectedIds.includes(lawyer.id);
			return {
				...lawyer,
				connected,
				name: getFullName([lawyer.firstName, lawyer.lastName]),
			};
		});
	}

	@Selector([TitlePlusState.connectedIds, SelectedMatter.assignedLawyer])
	static lawyerConnected(
		connectedIds: string[],
		assignedLawyer: MatterBusinessUser,
	): boolean {
		return connectedIds.includes(assignedLawyer?.id);
	}

	@Selector([TitlePlusState.connectedLawyers])
	static connectedLawyerIds(lawyers: BusinessUser[]): string[] {
		return lawyers.map((lawyer) => lawyer.id);
	}

	@Selector([TitlePlusState])
	static errored(state: TitlePlusStateModel): boolean {
		return !!state?.error;
	}

	@Selector([TitlePlusState])
	static loaded(state: TitlePlusStateModel): boolean {
		return state?.loaded;
	}

	@Selector([TitlePlusState, AuthState.getLoading])
	static loading(state: TitlePlusStateModel, authLoading: boolean): boolean {
		return !!(state.loading || authLoading);
	}

	@Selector([
		TitlePlusState.connectedLawyers,
		TitlePlusState.isUserConnectedThemself,
	])
	static connected(lawyers: ConnectedUser[], themself: boolean): boolean {
		return !!(themself || lawyers.filter((lawyer) => lawyer.connected).length);
	}

	@Selector([TitlePlusState.connectedLawyers, AuthState.getUserId])
	static isUserConnectedThemself(
		connectedLawyers: ConnectedUser[],
		authId,
	): boolean {
		return !!connectedLawyers.find(
			(user) => user.id === authId && user.connected,
		);
	}

	@Action(Errored)
	errored({ patchState }: Ctx, { logData }: Errored): void {
		const msg = 'Error fetching TitlePLUS connection status';
		console.warn(msg, logData);
		LogRocket.error(msg, logData);
		patchState({
			error: true,
			loading: false,
			loaded: false,
		});
	}

	@Action(Loaded)
	loaded({ patchState }: Ctx, { connectedIds }: Loaded): void {
		const loaded = !!connectedIds.length;
		patchState({
			error: false,
			loaded,
			loading: false,
			connectedIds,
		});
	}

	@Action(Loading)
	loading({ patchState }: Ctx): void {
		patchState({ loading: true, error: false });
	}
}
